import { QuestionDifficulty } from "@/enums/contentTypes";
import { getSubjectColorIndex } from "@/lib/strapi/formatters";
import {
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Flex,
  Group,
  MultiSelect,
  Stack,
  Text,
  Title,
  TitleOrder,
} from "@mantine/core";
import {
  MainMetaMapSubject,
  MainMetaMapSubTopic,
  MainMetaMapTopic,
} from "@tsTypes/meta-schema";
import { getUserPreferredSubTopicTitle } from "@utils/routing";
import clsx from "clsx";
import { useMemo } from "react";
import SubjectIcon from "../../../../../../../../components/logos/SubjectIcon";
import { getQuestionDifficultyIcon } from "../../../../questions/[topic]/components/Header/QuestionDifficultyChip";
import formatStepClasses from "./TestFormatStep.module.css";
import classes from "./TestTypeAndContentStep.module.css";
import { convertTestTypeToNiceName } from "./TestPreviewStep";
import { MockExamType } from "./types";

export default function TestTypeAndContentStep({
  subjectOptions,
  selectedSubjectId,
  testType,
  onSelectSubject,
  onSelectTestType,
  topicOptions,
  subTopicOptions,
  selectedSubTopicDocumentIds,
  onSetSelectedSubTopicDocumentIds,
  selectedDifficulties,
  onToggleDifficulty,
}: StepMockExamTypeProps) {
  const perTopic = useMemo(() => {
    if (!topicOptions || !subTopicOptions) return null;

    type FlatItem = { value: string; label: string; group: "SL" | "HL" };
    const map: Record<
      string,
      {
        all: string[];
        sl: string[];
        hl: string[];
        data: Array<{
          group: "SL" | "HL";
          items: { value: string; label: string }[];
        }>;
        flat: FlatItem[];
      }
    > = {};

    Object.keys(topicOptions).forEach((topicDocumentId) => {
      map[topicDocumentId] = {
        all: [],
        sl: [],
        hl: [],
        data: [],
        flat: [],
      };
    });

    Object.entries(subTopicOptions).forEach(([subTopicDocumentId, meta]) => {
      const bucket = map[meta.topicDocumentId];
      if (!bucket) return;

      const group: "SL" | "HL" = meta.isHL ? "HL" : "SL";
      const label = getUserPreferredSubTopicTitle(meta);

      bucket.all.push(subTopicDocumentId);
      (meta.isHL ? bucket.hl : bucket.sl).push(subTopicDocumentId);
      bucket.flat.push({ value: subTopicDocumentId, label, group });
    });

    const labelCollator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: "base",
    });
    Object.values(map).forEach((bucket) => {
      bucket.flat.sort((a, b) => {
        if (a.group !== b.group) return a.group === "SL" ? -1 : 1;
        return labelCollator.compare(a.label, b.label);
      });
      const slItems = bucket.flat
        .filter((i) => i.group === "SL")
        .map(({ value, label }) => ({ value, label }));
      const hlItems = bucket.flat
        .filter((i) => i.group === "HL")
        .map(({ value, label }) => ({ value, label }));
      if (slItems.length) bucket.data.push({ group: "SL", items: slItems });
      if (hlItems.length) bucket.data.push({ group: "HL", items: hlItems });
    });

    return map;
  }, [topicOptions, subTopicOptions]);

  const setTopicSelection = (
    topicDocumentId: string,
    nextInTopic: string[]
  ) => {
    if (
      !perTopic ||
      !selectedSubTopicDocumentIds ||
      !onSetSelectedSubTopicDocumentIds
    ) {
      return;
    }
    const topicIds = new Set(perTopic[topicDocumentId]?.all ?? []);
    const outside = selectedSubTopicDocumentIds.filter(
      (id) => !topicIds.has(id)
    );
    onSetSelectedSubTopicDocumentIds([...outside, ...nextInTopic]);
  };
  return (
    <Stack className={classes.rootStack}>
      <div className={classes.sectionWrapper}>
        <Title order={5 as TitleOrder} className={classes.title}>
          1) Select subject
        </Title>
        <Flex className={classes.subjectOptionsFlex}>
          {Object.entries(subjectOptions).map(([documentId, title]) => {
            const subjectColorIndex = getSubjectColorIndex(title);
            const isSelected = selectedSubjectId === documentId;
            return (
              <Box
                key={documentId}
                className={clsx(
                  classes.subjectCard,
                  isSelected && classes.subjectCardSelected
                )}
                onClick={() => onSelectSubject(documentId)}
              >
                <SubjectIcon subjectColorIndex={subjectColorIndex} size="sm" />
                <Text size="sm" fw={600} c={subjectColorIndex}>
                  {title}
                </Text>
              </Box>
            );
          })}
        </Flex>
      </div>
      <Divider className={classes.divider} />
      {testType ? (
        <div className={classes.sectionWrapper}>
          <Title order={5 as TitleOrder} className={classes.title}>
            2) Select mock exam
          </Title>
          <Button.Group className={classes.buttonGroup}>
            <Button
              className={formatStepClasses.formatButton}
              color="darkblue"
              size="sm"
              variant={testType === "slMcPaper" ? "filled" : "outline"}
              onClick={() => onSelectTestType(MockExamType.slMcPaper)}
            >
              {convertTestTypeToNiceName(MockExamType.slMcPaper)}
            </Button>
            <Button
              className={formatStepClasses.formatButton}
              color="darkblue"
              size="sm"
              variant={testType === "hlMcPaper" ? "filled" : "outline"}
              onClick={() => onSelectTestType(MockExamType.hlMcPaper)}
            >
              {convertTestTypeToNiceName(MockExamType.hlMcPaper)}
            </Button>
            <Button
              className={formatStepClasses.formatButton}
              color="darkblue"
              size="sm"
              variant={testType === "slLaPaper" ? "filled" : "outline"}
              onClick={() => onSelectTestType(MockExamType.slLaPaper)}
            >
              {convertTestTypeToNiceName(MockExamType.slLaPaper)}
            </Button>
            <Button
              className={formatStepClasses.formatButton}
              color="darkblue"
              size="sm"
              variant={testType === "hlLaPaper" ? "filled" : "outline"}
              onClick={() => onSelectTestType(MockExamType.hlLaPaper)}
            >
              {convertTestTypeToNiceName(MockExamType.hlLaPaper)}
            </Button>
          </Button.Group>
        </div>
      ) : (
        <Stack className={classes.subtopicsStack}>
          <div className={classes.sectionWrapper}>
            <Title order={5 as TitleOrder} className={classes.title}>
              2) Select subtopics
            </Title>
            {Object.entries(topicOptions).map(
              ([topicDocumentId, topicMeta]) => {
                const bucket = perTopic?.[topicDocumentId];
                const allIds = bucket?.all ?? [];
                const slIds = bucket?.sl ?? [];
                const hlIds = bucket?.hl ?? [];
                const data = bucket?.data ?? [];

                const selectedInTopic = (
                  selectedSubTopicDocumentIds ?? []
                ).filter((id) => allIds.includes(id));
                const hasBoth = slIds.length > 0 && hlIds.length > 0;

                return (
                  <Card
                    key={topicDocumentId}
                    withBorder
                    className={classes.topicCard}
                  >
                    <Group className={classes.cardGroup}>
                      <Text className={classes.topicTitleText}>
                        {topicMeta.number} {topicMeta.title}
                      </Text>
                      <Badge
                        variant="filled"
                        color="darkblue"
                        className={classes.topicBadge}
                      >
                        {selectedInTopic.length} selected
                      </Badge>
                    </Group>

                    <Button.Group className={classes.quickActionButtonGroup}>
                      <Button
                        size="xs"
                        color="darkblue"
                        variant="white"
                        className={classes.quickActionButton}
                        onClick={() =>
                          setTopicSelection(topicDocumentId, allIds)
                        }
                      >
                        All
                      </Button>
                      {hasBoth ? (
                        <>
                          <Button
                            size="xs"
                            color="darkblue"
                            variant="white"
                            className={classes.quickActionButton}
                            onClick={() =>
                              setTopicSelection(topicDocumentId, slIds)
                            }
                          >
                            SL only
                          </Button>
                          <Button
                            size="xs"
                            color="darkblue"
                            variant="white"
                            className={classes.quickActionButton}
                            onClick={() =>
                              setTopicSelection(topicDocumentId, hlIds)
                            }
                          >
                            HL
                          </Button>
                        </>
                      ) : null}
                    </Button.Group>

                    <MultiSelect
                      size="sm"
                      searchable
                      clearable
                      data={data}
                      value={selectedInTopic}
                      onChange={(next) =>
                        setTopicSelection(topicDocumentId, next)
                      }
                      placeholder="Search subtopics…"
                      nothingFoundMessage="All subtopics selected"
                      maxDropdownHeight={250}
                      classNames={{
                        pill: classes.multiSelectValue,
                        pillsList: classes.multiSelectValues,
                        input: classes.multiSelectInput,
                      }}
                    />
                  </Card>
                );
              }
            )}
          </div>
          <Divider className={classes.divider} />
          <div className={classes.sectionWrapper}>
            <Title order={5 as TitleOrder} className={classes.title}>
              3) Select difficulties
            </Title>
            <Flex className={classes.difficultiesFlex}>
              {(["Easy", "Medium", "Hard"] as const).map((difficulty) => {
                const selected = (selectedDifficulties ?? []).includes(
                  difficulty
                );
                return (
                  <Button
                    key={difficulty}
                    color="darkblue"
                    size="sm"
                    variant={selected ? "filled" : "outline"}
                    onClick={() => onToggleDifficulty(difficulty)}
                    leftSection={getQuestionDifficultyIcon(difficulty, 18)}
                    className={classes.difficultyButton}
                  >
                    {difficulty}
                  </Button>
                );
              })}
            </Flex>
          </div>
        </Stack>
      )}
    </Stack>
  );
}

type StepMockExamTypeProps =
  | {
      subjectOptions: { [documentId: string]: MainMetaMapSubject["title"] };
      selectedSubjectId: string | null;
      testType?: undefined;
      onSelectSubject: (subjectId: string) => void;
      onSelectTestType?: undefined;
      topicOptions: {
        [topicDocumentId: string]: Omit<
          MainMetaMapTopic,
          "index" | "subjectDocumentId"
        >;
      };
      subTopicOptions: {
        [subTopicDocumentId: string]: Omit<
          MainMetaMapSubTopic,
          "index" | "subThemes"
        >;
      };
      selectedSubTopicDocumentIds: string[];
      onSetSelectedSubTopicDocumentIds: (next: string[]) => void;
      selectedDifficulties: QuestionDifficulty[];
      onToggleDifficulty: (difficulty: QuestionDifficulty) => void;
    }
  | {
      subjectOptions: { [documentId: string]: MainMetaMapSubject["title"] };
      selectedSubjectId: string | null;
      testType: MockExamType;
      onSelectSubject: (subjectId: string) => void;
      onSelectTestType: (value: MockExamType) => void;
      topicOptions?: undefined;
      subTopicOptions?: undefined;
      selectedSubTopicDocumentIds?: undefined;
      onSetSelectedSubTopicDocumentIds?: undefined;
      selectedDifficulties?: undefined;
      onToggleDifficulty?: undefined;
    };
